import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { trustRequestTransactionApi } from '../api/trustRequestTransactionApi';
import { trustRequestTransactionKeys } from './useTrustRequestTransactionsQuery';

interface BulkActionParams {
  juniperTRRIds: string[];
  reason?: string;
  emailTemplateOverrideId?: string;
}

interface BulkSimulateParams extends BulkActionParams {
  eventType: 'update' | 'delete';
}

const generateRequestId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const invalidateTransactions = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: trustRequestTransactionKeys.all });
};

export function useBulkTrrReleaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: BulkActionParams) => {
      const requestId = generateRequestId('bulk-trr-release');
      return trustRequestTransactionApi.bulkRelease({
        ...params,
        requestId,
      });
    },
    onSuccess: (response: any) => {
      showToast.success(response?.message || 'Queued release for selected trust requests');
      invalidateTransactions(queryClient);
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to queue releases'
      );
    },
  });
}

export function useBulkTrrResendMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: BulkActionParams) => {
      const requestId = generateRequestId('bulk-trr-follow-up');
      return trustRequestTransactionApi.bulkSendFollowUp({
        ...params,
        requestId,
      });
    },
    onSuccess: (response: any) => {
      showToast.success(response?.message || 'Queued follow-up for selected trust requests');
      invalidateTransactions(queryClient);
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to queue follow-up sends'
      );
    },
  });
}

export function useBulkTrrRegenerateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: BulkActionParams) => {
      const requestId = generateRequestId('bulk-trr-regenerate');
      return trustRequestTransactionApi.bulkRegeneratePdf({
        ...params,
        requestId,
      });
    },
    onSuccess: (response: any) => {
      showToast.success(response?.message || 'Queued PDF regeneration for selected trust requests');
      invalidateTransactions(queryClient);
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to queue PDF regeneration'
      );
    },
  });
}

export function useBulkTrrRefreshMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: BulkActionParams) => {
      const requestId = generateRequestId('bulk-trr-refresh');
      return trustRequestTransactionApi.bulkRefresh({
        ...params,
        requestId,
      });
    },
    onSuccess: (response: any) => {
      showToast.success(response?.message || 'Queued refresh for selected trust requests');
      invalidateTransactions(queryClient);
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to queue refresh'
      );
    },
  });
}

export function useBulkTrrSimulateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: BulkSimulateParams) => {
      const requestId = generateRequestId('bulk-trr-simulate');
      return trustRequestTransactionApi.bulkSimulate({
        ...params,
        requestId,
      });
    },
    onSuccess: (response: any) => {
      showToast.success(response?.message || 'Queued retry of last events for selected trust requests');
      invalidateTransactions(queryClient);
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to queue last event retries'
      );
    },
  });
}

export function useBulkTrrMarkCompletedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: BulkActionParams) => {
      const requestId = generateRequestId('bulk-trr-mark-completed');
      return trustRequestTransactionApi.bulkMarkCompleted({
        ...params,
        requestId,
      });
    },
    onSuccess: (response: any) => {
      const total = response?.total || 0;
      if (total > 0) {
        showToast.success(`${total} transaction(s) queued to mark as completed - check back in a few moments...`);
      } else if (response?.status === 'complete') {
        showToast.info('No trust requests to mark as completed');
      }
      invalidateTransactions(queryClient);
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to mark as completed'
      );
    },
  });
}


